import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { enablePush, getPushPermission, pushSupported } from '@/lib/push';
import { PermissionPromptModal } from '@/components/PermissionPromptModal';

function isPreviewHost() {
  if (typeof window === 'undefined') return true;
  const h = window.location.hostname;
  return h.startsWith('id-preview--') || h.startsWith('preview--')
    || h.endsWith('.lovableproject.com') || h.endsWith('.lovableproject-dev.com')
    || h.endsWith('.beta.lovable.dev') || h === 'localhost';
}

type Copy = { title: string; body: string };

const COPY: Record<string, Copy> = {
  foodpreneur: {
    title: 'Never miss an order',
    body: 'Get notified the moment a customer places an order — even when Sis Thoko is closed.',
  },
  community: {
    title: 'Know when your order is ready',
    body: 'Get notified when the seller confirms and prepares your order.',
  },
  supplier: {
    title: 'Get notified of new orders',
    body: 'Receive instant alerts when foodpreneurs order from your catalogue.',
  },
  default: {
    title: 'Turn on deal alerts',
    body: 'Sis Thoko hunts deals all week. Get an alert the moment a match is found — even when the app is closed.',
  },
};

/**
 * Permission gate — only triggers AFTER user takes meaningful action.
 *
 * Triggers per role:
 *  - foodpreneur: after a seller profile becomes live, OR after their first marketplace order
 *  - community: after their first marketplace order
 *  - supplier: after first login (no other prerequisite)
 *
 * Snoozes for 30 days when dismissed; never re-asks if browser denied.
 */
export function PushPermissionGate() {
  const { profile } = useAuth();
  const [open, setOpen] = useState(false);
  const [copy, setCopy] = useState<Copy>(COPY.default);

  useEffect(() => {
    if (!profile?.id) return;
    if (!pushSupported()) return;
    if (isPreviewHost()) return;

    let cancelled = false;
    (async () => {
      const permission = await getPushPermission();
      if (permission === 'denied') return;       // OS blocked — never re-ask
      if (permission === 'granted') return;      // already on; lib silently re-subscribes elsewhere

      // Read prefs from profile
      const prefs = (profile as any)?.notification_preferences || {};
      if (prefs.web_push_enabled) return;
      const snoozedUntil = prefs.web_push_snoozed_until ? new Date(prefs.web_push_snoozed_until).getTime() : 0;
      if (snoozedUntil && Date.now() < snoozedUntil) return;

      // Detect role + trigger eligibility
      const role = String(profile.role || '').toLowerCase();
      let eligible = false;
      let chosen = COPY.default;

      if (role === 'supplier') {
        eligible = true; // ask after first login
        chosen = COPY.supplier;
      } else {
        // Foodpreneur or community user — check for at least one order or a live seller profile
        const userId = profile.id;
        const [{ count: orderCount }, { data: seller }] = await Promise.all([
          supabase.from('master_orders').select('id', { count: 'exact', head: true }).eq('user_id', userId),
          supabase.from('seller_profiles').select('is_active').eq('user_id', userId).maybeSingle(),
        ]);
        const hasOrder = (orderCount ?? 0) > 0;
        const isLiveSeller = !!seller?.is_active;
        if (hasOrder || isLiveSeller) {
          eligible = true;
          chosen = isLiveSeller ? COPY.foodpreneur : (role === 'community' ? COPY.community : COPY.foodpreneur);
        }
      }

      if (!eligible || cancelled) return;
      setCopy(chosen);
      const t = setTimeout(() => { if (!cancelled) setOpen(true); }, 1500);
      return () => clearTimeout(t);
    })();

    return () => { cancelled = true; };
  }, [profile?.id, (profile as any)?.notification_preferences]);

  const handleAllow = async () => {
    const result = await enablePush();
    setOpen(false);
    if (!result.ok) {
      toast.error('Could not enable notifications', { description: result.error });
      return;
    }
    toast.success('Notifications enabled on this device.');
  };

  const handleDismiss = async () => {
    setOpen(false);
    if (!profile?.id) return;
    const prefs = ((profile as any)?.notification_preferences as Record<string, unknown>) || {};
    await supabase
      .from('profiles')
      .update({
        notification_preferences: {
          ...prefs,
          web_push_permission_asked: true,
          web_push_snoozed_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        },
      })
      .eq('id', profile.id);
  };

  return (
    <PermissionPromptModal
      shouldShow={open}
      title={copy.title}
      body={copy.body}
      onAllow={handleAllow}
      onDismiss={handleDismiss}
    />
  );
}
